import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Package } from "lucide-react";

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (color: any, size: string, productData: any) => void;
  products: any[];
  isLoading?: boolean;
}

const isValidImage = (url: any) => {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http') || url.startsWith('/') || url.startsWith('data:');
};

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`tw-relative tw-overflow-hidden tw-bg-[#1c1c1e] tw-rounded-md ${className}`}>
    <motion.div
      className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/5 tw-to-transparent"
      initial={{ x: "-100%" }}
      animate={{ x: "100%" }}
      transition={{
        duration: 1.6, repeat: Infinity, ease: "easeInOut",
      }}
    />
  </div>
);

const SkeletonCard = () => (
  <div className="tw-bg-[#131313] tw-rounded-[24px] tw-h-auto sm:tw-h-[320px] tw-overflow-hidden tw-border tw-border-white/5 tw-flex tw-flex-col sm:tw-flex-row">
    <div className="tw-w-full sm:tw-w-[260px] tw-shrink-0">
      <Skeleton className="tw-w-full tw-h-full tw-rounded-none" />
    </div>
    <div className="tw-p-5 tw-flex-1 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-space-y-2">
        <Skeleton className="tw-h-7 tw-w-3/4" />
        <Skeleton className="tw-h-2 tw-w-1/4" />
      </div>
      <div className="tw-space-y-2 tw-mt-2">
        <Skeleton className="tw-h-2 tw-w-16" />
        <div className="tw-flex tw-gap-2.5">
          <Skeleton className="tw-w-8 tw-h-8 tw-rounded-full" />
          <Skeleton className="tw-w-8 tw-h-8 tw-rounded-full" />
        </div>
      </div>
      <div className="tw-space-y-2 tw-mt-2">
        <Skeleton className="tw-h-2 tw-w-20" />
        <div className="tw-flex tw-gap-2">
          <Skeleton className="tw-w-14 tw-h-9 tw-rounded-lg" />
          <Skeleton className="tw-w-14 tw-h-9 tw-rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

const ProductCard: React.FC<{ 
  group: any, 
  onSelect: (color: any, size: string) => void,
  isCardSelected: boolean,
  activeSelectedSize: string | null,
  badge?: string,
  subtitle?: string
}> = ({ group, onSelect, isCardSelected, activeSelectedSize, badge, subtitle }) => {
  // Collect unique color variants for this product group by name
  const allVariants = group.products.flatMap((p: any) => p.colors);
  
  // Deduplicate colors based on name to avoid mapping twice
  const uniqueColors = allVariants.filter((color: any, index: number, self: any[]) =>
    index === self.findIndex((t) => t.name === color.name)
  );

  const [activeColor, setActiveColor] = useState(uniqueColors[0]);
  const [activeSize, setActiveSize] = useState<string | null>(activeSelectedSize);

  useEffect(() => {
    setActiveSize(activeSelectedSize);
  }, [activeSelectedSize]);

  const handleColorChange = (color: any) => {
    setActiveColor(color);
    setActiveSize(null);
  };

  const handleSizeSelect = (size: string) => {
    setActiveSize(size);
    onSelect(activeColor, size);
  };

  return (
    <div className={`tw-bg-[#131313] tw-rounded-[24px] tw-overflow-hidden tw-border tw-transition-all duration-500 tw-group tw-flex tw-flex-col sm:tw-flex-row tw-h-[320px] ${
      isCardSelected 
        ? "tw-border-[#00C5FB] tw-shadow-[0_0_50px_rgba(0,197,251,0.2)]" 
        : "tw-border-white/5 hover:tw-border-white/20"
    }`}>
      {/* Left: Image Preview */}
      <div className="tw-w-full sm:tw-w-[260px] tw-aspect-square sm:tw-aspect-auto tw-bg-black/60 tw-relative tw-overflow-hidden tw-shrink-0">
        <AnimatePresence mode="wait">
          {isValidImage(activeColor.image) ? (
            <motion.img 
              key={activeColor.name}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              src={activeColor.image} 
              alt={activeColor.name} 
              className="tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-1000 group-hover:tw-scale-110" 
            />
          ) : (
            <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-white/5">
              <Package className="tw-w-10 tw-h-10 tw-text-white/20" />
            </div>
          )}
        </AnimatePresence>
        {badge && (
          <div className="tw-absolute tw-top-4 tw-left-4 tw-px-2.5 tw-py-1 tw-rounded-lg tw-text-[8px] tw-font-black tw-tracking-widest tw-bg-[#00C5FB] tw-text-black tw-shadow-lg tw-z-20">
            {badge.toUpperCase()}
          </div>
        )}
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/40 tw-to-transparent tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
      </div>

      {/* Right: Info Section */}
      <div className="tw-p-5 tw-flex-1 tw-flex tw-flex-col tw-gap-4 tw-justify-between">
        <div className="tw-space-y-1">
          <h4 className="tw-text-white tw-font-bold tw-text-xl tw-tracking-tight tw-line-clamp-1">{group.title}</h4>
          <div className="tw-text-[8px] tw-font-black tw-tracking-[0.2em] tw-text-[#00C5FB]/80">
            {subtitle || "AVAILABLE NOW"}
          </div>
        </div>

        {/* Color Selection dots */}
        <div className="tw-space-y-2">
          <div className="tw-flex tw-justify-between tw-items-center">
             <span className="tw-text-[8px] tw-font-black tw-text-gray-500 tw-uppercase tw-tracking-widest">Variation</span>
             <span className="tw-text-[#00C5FB] tw-text-[9px] tw-font-bold">{activeColor.name}</span>
          </div>
          <div className="tw-flex tw-gap-3 tw-flex-wrap">
            {uniqueColors.map((color: any, idx: number) => (
              <button
                key={`${color.name}-${idx}`}
                onClick={() => handleColorChange(color)}
                className={`tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-p-0.5 tw-transition-all duration-300 ${
                  activeColor.name === color.name 
                    ? "tw-border-[#00C5FB] tw-ring-4 tw-ring-[#00C5FB]/10 tw-scale-110" 
                    : "tw-border-transparent hover:tw-border-white/30"
                }`}
              >
                {isValidImage(color.image) ? (
                  <div 
                    className="tw-w-full tw-h-full tw-rounded-full tw-bg-cover tw-bg-center"
                    style={{ backgroundImage: `url(${color.image})` }}
                  />
                ) : (
                  <div className="tw-w-full tw-h-full tw-rounded-full tw-bg-white/10 tw-flex tw-items-center tw-justify-center">
                    <Package className="tw-w-3 tw-h-3 tw-text-white/40" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Size Selection */}
        <div className="tw-space-y-2">
          <span className="tw-text-[8px] tw-font-black tw-text-gray-500 tw-uppercase tw-tracking-widest">Select Size</span>
          <div className="tw-flex tw-gap-2 tw-flex-wrap">
            {Object.keys(activeColor.sizes).map(sz => (
              <button
                key={sz}
                onClick={() => handleSizeSelect(sz)}
                className={`tw-min-w-[48px] tw-px-3 tw-py-2 tw-rounded-lg tw-text-[10px] tw-font-bold tw-transition-all duration-300 tw-border-2 ${
                  activeSize === sz
                    ? "tw-bg-[#00C5FB] tw-text-black tw-border-[#00C5FB] tw-shadow-[0_0_15px_rgba(0,197,251,0.3)]"
                    : "tw-bg-[#1c1c1e] tw-text-gray-400 tw-border-transparent hover:tw-border-white/10"
                }`}
              >
                {sz}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  products = [],
  isLoading = false,
}) => {
  const [selection, setSelection] = useState<{group: any, color: any, size: string} | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelection(null);
    }
  }, [isOpen]);

  const handleSelection = (group: any, color: any, size: string) => {
    setSelection({ group, color, size });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-p-4 md:tw-p-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/95 tw-backdrop-blur-xl"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 30 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-4xl tw-h-[90vh] tw-max-h-[850px] tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/5 tw-flex tw-flex-col"
          >
            {/* Header */}
            <div className="tw-px-10 tw-py-8 tw-flex tw-justify-between tw-items-start tw-bg-[#0d0d0d]">
              <div className="tw-space-y-1">
                <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-tracking-tight">Product Catalog</h2>
                <p className="tw-text-gray-500 tw-text-sm">Explore our collection and select a replacement product.</p>
              </div>
              <button onClick={onClose} className="tw-p-2 tw-text-gray-500 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer">
                <X className="tw-w-6 tw-h-6" />
              </button>
            </div>

            {/* Catalog Grid */}
            <div className="tw-flex-1 tw-overflow-y-auto tw-px-10 tw-pb-10 custom-scrollbar-mini">
              {isLoading ? (
                <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6">
                  {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6">
                  {products.map((group, idx) => (
                    <ProductCard 
                      key={group.title}
                      group={group}
                      onSelect={(color, size) => handleSelection(group, color, size)}
                      isCardSelected={selection?.group.title === group.title}
                      activeSelectedSize={selection?.group.title === group.title ? selection.size : null}
                      badge={idx === 0 ? "Featured" : undefined}
                      subtitle={idx === 0 ? "RECOMMENDED CHOICE" : "LATEST CATALOG"}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer Action */}
            <div className="tw-p-10 tw-pt-6 tw-border-t tw-border-white/5 tw-bg-[#0a0a0a]/50 tw-backdrop-blur-md tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex-1">
                 <AnimatePresence mode="wait">
                   {selection ? (
                     <motion.div 
                       initial={{ opacity: 0, x: -20 }}
                       animate={{ opacity: 1, x: 0 }}
                       exit={{ opacity: 0, x: -20 }}
                       className="tw-flex tw-items-center tw-gap-5 tw-bg-white/5 tw-pr-6 tw-pl-2 tw-py-2 tw-rounded-2xl tw-border tw-border-white/10 tw-w-fit"
                     >
                        {isValidImage(selection.color.image) ? (
                          <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-cover tw-bg-center tw-border tw-border-white/10" style={{ backgroundImage: `url(${selection.color.image})` }} />
                        ) : (
                          <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-white/5 tw-border tw-border-white/10 tw-flex tw-items-center tw-justify-center">
                            <Package className="tw-w-6 tw-h-6 tw-text-white/20" />
                          </div>
                        )}
                        <div className="tw-flex tw-flex-col">
                          <span className="tw-text-white tw-font-bold tw-text-sm">{selection.group.title}</span>
                          <span className="tw-text-[#00C5FB] tw-text-[10px] tw-font-black tw-uppercase tw-tracking-widest">{selection.color.name} • Size {selection.size}</span>
                        </div>
                     </motion.div>
                   ) : (
                     <p className="tw-text-gray-500 tw-text-xs tw-font-medium">Please select a product and size to continue</p>
                   )}
                 </AnimatePresence>
              </div>
              <button 
                onClick={() => selection && onConfirm(selection.color, selection.size, selection.group)}
                disabled={!selection}
                className="tw-px-10 tw-py-4 tw-rounded-2xl tw-bg-[#00C5FB] tw-text-black tw-font-black tw-text-[11px] tw-uppercase tw-tracking-widest tw-border-none tw-cursor-pointer hover:tw-bg-white tw-transition-all duration-500 tw-shadow-[0_15px_30px_-10px_rgba(0,197,251,0.4)] disabled:tw-opacity-20 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-3"
              >
                Confirm Replacement <ArrowRight className="tw-w-4 tw-h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProductSelectionModal;
